import { Head, Link, router } from "@inertiajs/react"
import { Boxes, Building2, ChevronRight, Factory } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Select } from "@/components/ui/select"
import { OwnBadge, Pagination, ReviewButton, SearchForm, type PageMeta } from "./shared"

type ManufacturerRow = {
  id: number
  name: string
  is_own: boolean
  brand_count: number
  leaf_count: number
}

type Props = {
  manufacturers: ManufacturerRow[]
  pagination: PageMeta
  filters: { search: string | null; owned: string | null }
  stats: { manufacturers: number; brands: number; leaves: number }
}

const DESCRIPTION =
  "Hierarki produk kanonik — manufaktur → merek → produk (leaf ukuran SKU) — yang menyatukan setiap nama produk dari semua dataset menjadi satu identitas."

export default function Index({ manufacturers, pagination, filters, stats }: Props) {
  const path = "/admin/product-master"
  const query = { owned: filters.owned, search: filters.search }

  return (
    <>
      <Head title="Produk Master">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Produk Master" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell width="wide">
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>Produk Master</h1>
              <p className="mt-1 text-sm text-ink-muted">{DESCRIPTION}</p>
            </div>
            <ReviewButton />
          </div>
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-ink-muted">
            <span className="inline-flex items-center gap-1">
              <Factory className="h-3.5 w-3.5" aria-hidden="true" /> {stats.manufacturers} manufaktur
            </span>
            <span className="inline-flex items-center gap-1">
              <Building2 className="h-3.5 w-3.5" aria-hidden="true" /> {stats.brands} merek
            </span>
            <span className="inline-flex items-center gap-1">
              <Boxes className="h-3.5 w-3.5" aria-hidden="true" /> {stats.leaves} produk (leaf)
            </span>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <SearchForm path={path} query={query} initial={filters.search ?? ""} placeholder="Nama manufaktur" />
          <div className="min-w-[170px]">
            <label htmlFor="owned">Kepemilikan</label>
            <Select
              id="owned"
              className="mt-1"
              value={filters.owned ?? ""}
              onChange={(e) =>
                router.get(
                  path,
                  { search: filters.search ?? "", owned: e.target.value },
                  { preserveState: true, preserveScroll: true, replace: true },
                )
              }
            >
              <option value="">Semua</option>
              <option value="true">Perusahaan sendiri</option>
              <option value="false">Kompetitor</option>
            </Select>
          </div>
        </div>

        {manufacturers.length === 0 ? (
          <div className="mt-10 rounded-lg border border-hairline bg-surface p-10 text-center">
            <p className="text-ink-muted">Tidak ada manufaktur yang cocok dengan filter.</p>
          </div>
        ) : (
          <>
            <div className="mt-6 hidden overflow-x-auto rounded-lg border border-hairline md:block">
              <table className="w-full text-sm">
                <thead className="bg-surface text-left text-ink-muted">
                  <tr>
                    <th className="px-4 py-3 font-medium">Manufaktur</th>
                    <th className="px-4 py-3 font-medium">Merek</th>
                    <th className="px-4 py-3 font-medium">Produk (leaf)</th>
                    <th className="px-4 py-3 font-medium">Penanda</th>
                    <th className="w-10 px-4 py-3" aria-label="Buka" />
                  </tr>
                </thead>
                <tbody>
                  {manufacturers.map((m) => (
                    <tr key={m.id} className="border-t border-hairline hover:bg-surface">
                      <td className="px-4 py-3">
                        <Link href={`${path}?manufacturer_id=${m.id}`} className="font-medium">
                          {m.name}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-ink-body">{m.brand_count}</td>
                      <td className="px-4 py-3 text-ink-body">{m.leaf_count}</td>
                      <td className="px-4 py-3">
                        <OwnBadge isOwn={m.is_own} />
                      </td>
                      <td className="px-4 py-3 text-ink-muted">
                        <Link href={`${path}?manufacturer_id=${m.id}`} aria-label={`Buka ${m.name}`}>
                          <ChevronRight className="h-4 w-4" aria-hidden="true" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex flex-col gap-3 md:hidden">
              {manufacturers.map((m) => (
                <Link
                  key={m.id}
                  href={`${path}?manufacturer_id=${m.id}`}
                  className="rounded-lg border border-hairline bg-surface p-4"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-ink-body">{m.name}</span>
                    <OwnBadge isOwn={m.is_own} />
                  </div>
                  <div className="mt-1 text-xs text-ink-muted">
                    {m.brand_count} merek · {m.leaf_count} produk
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}

        <Pagination meta={pagination} path={path} query={query} />
      </AppShell>
    </>
  )
}
